import React, { useMemo, useState } from "react";
import { BarChart3 } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickSport = (trainingData) => {
  const bySport =
    trainingData?.bySport && typeof trainingData.bySport === "object"
      ? trainingData.bySport
      : {};

  const sports = Object.keys(bySport);
  if (sports.length === 0) return { sport: "", fields: {} };

  const preferredSport =
    trainingData?.lastSport && bySport[trainingData.lastSport]
      ? trainingData.lastSport
      : sports[0];

  return {
    sport: preferredSport,
    fields: bySport[preferredSport]?.fields || {},
  };
};

const CHAR_WIDTH = 0.55; // average glyph width, as a fraction of font size
const LABEL_GAP = 10; // px of air kept between neighbouring labels
const MAX_FONT = 10;
const MIN_FONT = 8;

const clip = (text, maxChars) =>
  text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;

const fillLines = (words, width) =>
  words.reduce((lines, word) => {
    const last = lines[lines.length - 1];
    if (last !== undefined && last.length + 1 + word.length <= width) {
      lines[lines.length - 1] = `${last} ${word}`;
    } else {
      lines.push(word);
    }
    return lines;
  }, []);

// Wraps on word boundaries (a word is never split) into at most `maxLines`
// lines of `maxChars`. Among the wraps with the fewest lines it takes the
// narrowest, so units stay with their number ("Sprint" / "30 m", not
// "Sprint 30" / "m").
const wrapLabel = (text, maxChars, maxLines = 3) => {
  const words = text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => clip(word, maxChars));
  if (words.length === 0) return [];

  const lines = fillLines(words, maxChars);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = clip(`${kept[maxLines - 1]}…`, maxChars);
    return kept;
  }
  const longest = Math.max(...words.map((word) => word.length));
  for (let width = longest; width < maxChars; width += 1) {
    const narrower = fillLines(words, width);
    if (narrower.length === lines.length) return narrower;
  }
  return lines;
};

// One font size and wrap width shared by every label, from the room each bar
// has: the longest word must fit whole, so the type only shrinks as far as needed.
const getLabelLayout = (labels, chartWidth) => {
  const band = chartWidth ? (chartWidth - 10) / Math.max(labels.length, 1) : 60;
  const usable = Math.max(band - LABEL_GAP, 24);
  const longestWord = Math.max(
    1,
    ...labels.flatMap((label) => label.split(/\s+/).map((word) => word.length)),
  );
  const fontSize = Math.min(
    MAX_FONT,
    Math.max(MIN_FONT, usable / (longestWord * CHAR_WIDTH)),
  );
  return {
    fontSize,
    maxChars: Math.max(4, Math.round(usable / (fontSize * CHAR_WIDTH))),
  };
};

const WrappedTick = ({ x, y, payload, fontSize, maxChars }) => (
  <text x={x} y={y} textAnchor="middle" fill="#9ca3af" fontSize={fontSize}>
    {wrapLabel(payload.value, maxChars).map((line, index) => (
      <tspan
        key={index}
        x={x}
        dy={index === 0 ? fontSize + 2 : fontSize * 1.15}
      >
        {line}
      </tspan>
    ))}
  </text>
);

const ExerciseSnapshotWidget = ({ data }) => {
  const { sport, chartData } = useMemo(() => {
    const selected = pickSport(data);

    const rows = Object.entries(selected.fields)
      .map(([fieldKey, field]) => {
        const numericValue = toNumber(field?.latest?.value);
        if (numericValue === null) return null;

        return {
          label: String(field?.title || fieldKey),
          value: numericValue,
          unit: field?.unit || "",
        };
      })
      .filter(Boolean)
      .slice(0, 6);

    return {
      sport: selected.sport,
      chartData: rows,
    };
  }, [data]);

  const [chartWidth, setChartWidth] = useState(0);
  const labelLayout = useMemo(
    () => getLabelLayout(chartData.map((row) => row.label), chartWidth),
    [chartData, chartWidth],
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-full bg-gray-50">
          <BarChart3 className="text-blue-600" size={20} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Resultados Ejercicios</h3>
          <p className="text-xs text-gray-500">{sport || "Sin deporte"}</p>
        </div>
      </div>

      {chartData.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          Sin valores numéricos para comparar.
        </p>
      ) : (
        <div className="h-44 mt-2">
          <ResponsiveContainer
            width="100%"
            height="100%"
            onResize={(width) => setChartWidth(width)}
          >
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={<WrappedTick {...labelLayout} />}
                tickLine={false}
                axisLine={false}
                interval={0}
                height={48}
              />
              <YAxis hide />
              <Tooltip
                formatter={(value, _name, payload) => [
                  `${value}${payload?.payload?.unit ? ` ${payload.payload.unit}` : ""}`,
                  payload?.payload?.label || "Valor",
                ]}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              />
              <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ExerciseSnapshotWidget;
